import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import RealtimeNotificationPanel from './RealtimeNotificationPanel';
import { useAuth } from '../../contexts/AuthContext';
import { trialService } from '../../services/trialService';
import ThemeToggle from './ThemeToggle';
import LanguageDropdown from '../LanguageDropdown';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';


const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(false);
  const { companySettings } = useCompanySettings();

  // Load trial status for authenticated users
  useEffect(() => {
    if (user?.id) {
      loadTrialStatus();
    }
  }, [user]);

  const loadTrialStatus = async () => {
    if (!user?.id) return;
    
    setLoadingTrial(true);
    try {
      const status = await trialService?.checkTrialStatus(user?.id);
      setTrialStatus(status);
    } catch (error) {
      console.error('Failed to load trial status:', error);
    } finally {
      setLoadingTrial(false);
    }
  };

  const handleAlertClick = (alert) => {
    // Navigate to relevant page based on alert type
    switch (alert?.alertType) {
      case 'nasfund_deadline': case'swt_filing_error': navigate('/payroll-compliance');
        break;
      case 'compliance_violation': navigate('/compliance-overview');
        break;
      case 'infrastructure_alert': navigate('/critical-infrastructure-monitoring-alert-command-center');
        break;
      case 'predictive_failure': navigate('/ai-powered-system-intelligence-predictive-analytics-center');
        break;
      default:
        break;
    }
  };

  // Primary navigation items (always visible on desktop)
  const primaryNavItems = [
    {
      label: 'Home',
      path: '/',
      icon: 'Home',
      tooltip: 'Marketing website landing page'
    },
    {
      label: 'Dashboard',
      path: '/main-dashboard-hub',
      icon: 'LayoutGrid',
      tooltip: 'Main Dashboard Hub - Central command center'
    },
    {
      label: 'Compliance',
      path: '/compliance-overview',
      icon: 'Shield',
      tooltip: 'Compliance overview dashboard with real-time monitoring'
    },
    {
      label: 'Workforce',
      path: '/workforce-analytics',
      icon: 'Users',
      tooltip: 'Workforce analytics with demographic insights'
    },
    {
      label: 'Payroll',
      path: '/payroll-compliance',
      icon: 'DollarSign',
      tooltip: 'Payroll compliance with automated calculations'
    },
    {
      label: 'Employees',
      path: '/employee-management',
      icon: 'User',
      tooltip: 'Employee management and directory'
    },
    {
      label: 'Self-Service',
      path: '/employee-self-service-portal',
      icon: 'UserCheck',
      tooltip: 'Employee self-service portal'
    }
  ];

  // Secondary navigation items (in "More" dropdown)
  const secondaryNavItems = [
    // Client & Organization Management
    {
      label: 'Client Admin',
      path: '/client-administration-dashboard',
      icon: 'Building',
      tooltip: 'Client administration dashboard'
    },
    {
      label: 'Multi-Client Overview',
      path: '/multi-client-organization-overview-dashboard',
      icon: 'Building2',
      tooltip: 'Multi-client organization overview'
    },
    {
      label: 'Company Accounts',
      path: '/company-accounts-management',
      icon: 'Briefcase',
      tooltip: 'Company accounts management'
    },
    {
      label: 'Client Onboarding',
      path: '/client-onboarding-progress-tracking-dashboard',
      icon: 'Activity',
      tooltip: 'Client onboarding progress tracking'
    },
    {
      label: 'Quick Setup Wizard',
      path: '/quick-setup-configuration-wizard',
      icon: 'Zap',
      tooltip: 'Quick Setup Configuration Wizard'
    },
    
    // Employee Lifecycle Management
    {
      label: 'Employee Onboarding',
      path: '/employee-onboarding-workflow-center',
      icon: 'UserPlus',
      tooltip: 'Employee onboarding workflow'
    },
    {
      label: 'Employee Offboarding',
      path: '/employee-offboarding-exit-management',
      icon: 'UserMinus',
      tooltip: 'Employee offboarding and exit management'
    },
    {
      label: 'Performance & Development',
      path: '/performance-staff-development-hub',
      icon: 'TrendingUp',
      tooltip: 'Performance & staff development'
    },
    {
      label: 'Leave Management',
      path: '/leave-management',
      icon: 'Calendar',
      tooltip: 'Leave management with balance tracking'
    },
    
    // Payroll & Financial Management
    {
      label: 'Payroll Processing Engine',
      path: '/advanced-payroll-processing-engine',
      icon: 'Calculator',
      tooltip: 'Advanced payroll processing engine'
    },
    {
      label: 'Payroll Management',
      path: '/payroll-management',
      icon: 'Wallet',
      tooltip: 'Payroll management and processing'
    },
    {
      label: 'Payroll Banking Control',
      path: '/payroll-processing-banking-control-center',
      icon: 'Landmark',
      tooltip: 'Payroll processing banking control center'
    },
    {
      label: 'Departmental Budget',
      path: '/departmental-budget-management',
      icon: 'PiggyBank',
      tooltip: 'Departmental budget tracking'
    },
    
    // Time & Attendance
    {
      label: 'Timesheet Management',
      path: '/timesheet-management-portal',
      icon: 'Clock',
      tooltip: 'Timesheet management portal'
    },
    {
      label: 'Time Machine Integration',
      path: '/time-machine-integration-hub',
      icon: 'Fingerprint',
      tooltip: 'Hikvision Time Machine integration'
    },
    
    // Document Management
    {
      label: 'Document Center',
      path: '/document-management-center',
      icon: 'FileText',
      tooltip: 'Document management center'
    },
    {
      label: 'Document Expiration Monitor',
      path: '/document-expiration-monitor',
      icon: 'AlertTriangle',
      tooltip: 'Document expiration monitor'
    },
    {
      label: 'Renewal Request Tracker',
      path: '/renewal-request-tracker',
      icon: 'Bell',
      tooltip: 'Renewal request tracker'
    },
    {
      label: 'Renewal Batch Coordinator',
      path: '/document-renewal-batch-coordinator',
      icon: 'FolderSync',
      tooltip: 'Document renewal batch coordinator'
    },
    {
      label: 'HR Document Review',
      path: '/hr-document-review-approval-center',
      icon: 'FileCheck',
      tooltip: 'HR document review and approval'
    },
    {
      label: 'Document Upload Portal',
      path: '/employee-document-upload-portal',
      icon: 'Upload',
      tooltip: 'Employee document upload portal'
    },
    
    // Compliance & Legal
    {
      label: 'Compliance Library',
      path: '/compliance-library',
      icon: 'BookOpen',
      tooltip: 'Compliance library with regulations'
    },
    {
      label: 'AI Compliance Analyzer',
      path: '/ai-powered-payroll-compliance-analyzer',
      icon: 'Sparkles',
      tooltip: 'AI-powered compliance analyzer'
    },
    {
      label: 'Violation Detection Center',
      path: '/real-time-employment-act-violation-detection-center',
      icon: 'ShieldAlert',
      tooltip: 'Real-time violation detection center'
    },
    {
      label: 'Violation Analytics',
      path: '/violation-analytics-prevention-dashboard',
      icon: 'BarChart',
      tooltip: 'Violation analytics and prevention dashboard'
    },
    {
      label: 'Violation Simulator',
      path: '/violation-simulator-testing-control-center',
      icon: 'TestTube',
      tooltip: 'Violation simulator and testing control center'
    },
    {
      label: 'Data Security Monitor',
      path: '/data-security-audit-trail-monitor',
      icon: 'Shield',
      tooltip: 'Data security and audit trail monitoring'
    },
    {
      label: 'Client Violation Alerts',
      path: '/client-violation-alert-response-portal',
      icon: 'BellRing',
      tooltip: 'Client violation alert & response portal'
    },
    {
      label: 'Contract Management',
      path: '/contract-management',
      icon: 'FileSignature',
      tooltip: 'Contract management'
    },
    {
      label: 'Employment Contracts',
      path: '/employment-contracts-legal-compliance-center',
      icon: 'Scale',
      tooltip: 'Employment contracts & legal compliance'
    },
    {
      label: 'Policy Library',
      path: '/policy-library-management',
      icon: 'FileEdit',
      tooltip: 'Policy library management'
    },
    {
      label: 'Approval Workflows',
      path: '/approval-management',
      icon: 'CheckSquare',
      tooltip: 'Approval workflows'
    },
    
    // Staff ID & Printing
    {
      label: 'Staff ID Management',
      path: '/staff-id-card-management-center',
      icon: 'CreditCard',
      tooltip: 'Staff ID card management'
    },
    {
      label: 'ID Card Design Studio',
      path: '/id-card-design-studio',
      icon: 'Palette',
      tooltip: 'ID card design studio'
    },
    {
      label: 'Printer Production Hub',
      path: '/printer-management-production-hub',
      icon: 'Printer',
      tooltip: 'Printer management & production'
    },
    
    // Analytics & Reports
    {
      label: 'Custom Report Builder',
      path: '/custom-compliance-report-builder',
      icon: 'FileBarChart',
      tooltip: 'Custom compliance report builder'
    },
    {
      label: 'User Analytics',
      path: '/user-analytics-dashboard',
      icon: 'Activity',
      tooltip: 'User analytics dashboard'
    },
    {
      label: 'Widget Analytics',
      path: '/widget-usage-analytics-dashboard',
      icon: 'BarChart2',
      tooltip: 'Widget usage analytics'
    },
    {
      label: 'Lifecycle Analytics',
      path: '/onboarding-offboarding-analytics-hub',
      icon: 'BarChart3',
      tooltip: 'Onboarding & offboarding analytics'
    },
    {
      label: 'Executive Board Dashboard',
      path: '/executive-board-dashboard',
      icon: 'BarChart4',
      tooltip: 'Executive board dashboard'
    },
    {
      label: 'Audit Trail',
      path: '/audit-trail',
      icon: 'FileSearch',
      tooltip: 'Audit trail'
    },
    
    // System Integration
    {
      label: 'ERP Integration Hub',
      path: '/erp-integration-hub',
      icon: 'Database',
      tooltip: 'ERP integration hub'
    },
    {
      label: 'Data Export Manager',
      path: '/data-export-integration-manager',
      icon: 'Download',
      tooltip: 'Data export and integration manager'
    },
    {
      label: 'Payroll Sync Center',
      path: '/payroll-system-synchronization-center',
      icon: 'RefreshCw',
      tooltip: 'Payroll system synchronization'
    },
    {
      label: 'Infrastructure Monitoring',
      path: '/infrastructure-monitoring-performance-dashboard',
      icon: 'Server',
      tooltip: 'Infrastructure monitoring'
    },
    {
      label: 'Error Monitoring',
      path: '/system-error-monitoring-recovery-center',
      icon: 'AlertCircle',
      tooltip: 'System error monitoring'
    },
    {
      label: 'Email Testing Center',
      path: '/email-testing-delivery-monitoring-center',
      icon: 'Mail',
      tooltip: 'Email testing & delivery monitoring center'
    },
    {
      label: 'AI Root Cause Analysis',
      path: '/anthropic-ai-root-cause-analysis-engine',
      icon: 'Brain',
      tooltip: 'AI-powered root cause analysis with Anthropic'
    },
    
    // Support & Communication
    {
      label: 'Support Ticket Center',
      path: '/support-ticket-management-center',
      icon: 'Headphones',
      tooltip: 'Support ticket management'
    },
    {
      label: 'Client Support Portal',
      path: '/client-support-portal-interface',
      icon: 'MessageSquare',
      tooltip: 'Client support portal'
    },
    {
      label: 'Support Analytics',
      path: '/support-analytics-performance-dashboard',
      icon: 'LineChart',
      tooltip: 'Support analytics'
    },
    {
      label: 'Notification Center',
      path: '/centralized-notification-center',
      icon: 'Inbox',
      tooltip: 'Centralized notification center'
    },
    {
      label: 'Email Delivery Monitoring',
      path: '/invoice-email-monitoring-delivery-dashboard',
      icon: 'Mail',
      tooltip: 'Invoice email monitoring dashboard'
    },
    
    // Sales & Marketing
    {
      label: 'Sales Lead Tracking',
      path: '/sales-lead-tracking-dashboard',
      icon: 'Target',
      tooltip: 'Sales lead tracking'
    },
    {
      label: 'Lead Source Attribution',
      path: '/lead-source-analytics-attribution-hub',
      icon: 'TrendingUp',
      tooltip: 'Lead source analytics'
    },
    {
      label: 'Deal Management',
      path: '/deal-management-opportunity-center',
      icon: 'Handshake',
      tooltip: 'Deal management'
    },
    {
      label: 'Pricing Plans',
      path: '/pricing-plans-subscription-center',
      icon: 'Tag',
      tooltip: 'Pricing plans and subscriptions'
    },
    {
      label: 'Customer Success Stories',
      path: '/customer-success-stories-roi-calculator',
      icon: 'Award',
      tooltip: 'Customer success stories'
    },
    {
      label: 'Testimonial Showcase',
      path: '/public-testimonial-showcase-portal',
      icon: 'Quote',
      tooltip: 'Public testimonial showcase'
    },
    {
      label: 'Review Management',
      path: '/client-review-testimonial-management-center',
      icon: 'Star',
      tooltip: 'Client review management'
    },
    {
      label: 'Review Analytics',
      path: '/review-analytics-success-metrics-dashboard',
      icon: 'StarHalf',
      tooltip: 'Review analytics'
    },
    {
      label: 'Product Demo Hub',
      path: '/product-demo-onboarding-hub',
      icon: 'Play',
      tooltip: 'Product demo and onboarding'
    },
    
    // Billing & Payments
    {
      label: 'Billing & Subscriptions',
      path: '/billing-subscription-management',
      icon: 'Receipt',
      tooltip: 'Billing and subscription management'
    },
    {
      label: 'Payment Settings',
      path: '/payment-billing-settings',
      icon: 'Settings',
      tooltip: 'Payment and billing settings'
    },
    {
      label: 'Payment Verification',
      path: '/manual-payment-verification-account-activation-center',
      icon: 'CheckCircle',
      tooltip: 'Manual payment verification'
    },
    {
      label: 'Subscription Monitoring',
      path: '/subscription-payment-monitoring-center',
      icon: 'Eye',
      tooltip: 'Subscription payment monitoring'
    },
    {
      label: 'Automated Payments',
      path: '/automated-payment-monitoring-dashboard',
      icon: 'Zap',
      tooltip: 'Automated payment monitoring'
    },
    {
      label: 'Payment Contact',
      path: '/payment-contact',
      icon: 'Phone',
      tooltip: 'Billing contact information'
    },
    {
      label: 'Checkout Portal',
      path: '/subscription-checkout-payment-portal',
      icon: 'ShoppingCart',
      tooltip: 'Subscription checkout'
    },
    {
      label: 'Client Value Analytics',
      path: '/client-value-analytics-dashboard',
      icon: 'DollarSign',
      tooltip: 'Client value analytics'
    },
    
    // Resources & Training
    {
      label: 'Training Academy',
      path: '/government-training-academy',
      icon: 'GraduationCap',
      tooltip: 'Government training academy'
    },
    {
      label: 'Help Center',
      path: '/in-app-help-center',
      icon: 'HelpCircle',
      tooltip: 'In-app help center'
    },
    {
      label: 'PNG Government News',
      path: '/government-news-updates-center',
      icon: 'Newspaper',
      tooltip: 'PNG government news updates'
    },
    {
      label: 'Workforce Calendar',
      path: '/strategic-workforce-calendar',
      icon: 'CalendarDays',
      tooltip: 'Strategic workforce calendar'
    },
    
    // Administration
    {
      label: 'Admin Inbox Dashboard',
      path: '/centralized-admin-inbox-dashboard',
      icon: 'Inbox',
      tooltip: 'Centralized admin inbox for all notifications'
    },
    {
      label: 'Cloud Storage Management',
      path: '/secure-cloud-storage-management-center',
      icon: 'Cloud',
      tooltip: 'Secure cloud storage management center'
    },
    {
      label: 'System Administration & Monitoring',
      path: '/system-administration-control-center',
      icon: 'Settings',
      tooltip: 'System administration control center'
    },
    {
      label: 'Account Verification',
      path: '/account-verification-management-center',
      icon: 'ShieldCheck',
      tooltip: 'Account verification management center'
    },
    {
      label: 'Auth Recovery & System Repair',
      path: '/authentication-recovery-system-repair-center',
      icon: 'Wrench',
      tooltip: 'Authentication recovery and system repair center'
    },
    {
      label: 'Team Access Management',
      path: '/team-access-management',
      icon: 'Users',
      tooltip: 'Team access management'
    },
    {
      label: 'Company Settings',
      path: '/company-settings',
      icon: 'Wrench',
      tooltip: 'Company settings'
    },
    {
      label: 'Email Notification Settings',
      path: '/email-notification-settings',
      icon: 'MailOpen',
      tooltip: 'Email notification settings'
    },
    {
      label: 'Customization Requests',
      path: '/client-customization-request-manager',
      icon: 'Paintbrush',
      tooltip: 'Client customization requests'
    },
    {
      label: 'Bulk Employee Import',
      path: '/bulk-employee-import',
      icon: 'UploadCloud',
      tooltip: 'Bulk employee import'
    },
    {
      label: 'Department Setup Wizard',
      path: '/government-department-setup-wizard',
      icon: 'Wand2',
      tooltip: 'Government department setup wizard'
    }
  ];

  const allNavigationItems = [...primaryNavItems, ...secondaryNavItems];

  const isActive = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMoreMenu = () => {
    setMoreMenuOpen(!moreMenuOpen);
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:bg-slate-900/95 dark:supports-[backdrop-filter]:bg-slate-900/80">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo and Brand */}
            <div className="flex items-center gap-3">
              <Link to="/main-dashboard-hub" className="flex items-center gap-3 group">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-sm group-hover:shadow-md transition-all">
                  <Icon name="Building2" size={20} className="text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                    {companySettings?.companyName || 'PNG HR Analytics'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">Professional HR Platform</span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {primaryNavItems?.map((item) => {
                const isActive = location?.pathname === item?.path;
                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    title={item?.tooltip}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive 
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={16} />
                    <span>{item?.label}</span>
                  </Link>
                );
              })}

              {/* More Menu */}
              <div className="relative">
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800 transition-all"
                >
                  <Icon name="MoreHorizontal" size={16} />
                  <span>More</span>
                  <Icon name={moreMenuOpen ? "ChevronUp" : "ChevronDown"} size={14} />
                </button>

                {moreMenuOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 max-h-[600px] overflow-y-auto z-50">
                    <div className="p-2">
                      {secondaryNavItems?.map((item) => {
                        const isActive = location?.pathname === item?.path;
                        return (
                          <Link
                            key={item?.path}
                            to={item?.path}
                            title={item?.tooltip}
                            onClick={() => setMoreMenuOpen(false)}
                            className={`
                              flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all whitespace-normal
                              ${isActive 
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700'
                              }
                            `}
                          >
                            <Icon name={item?.icon} size={16} className="flex-shrink-0" />
                            <span className="flex-1">{item?.label}</span>
                          </Link>
                        );
                      })}
                      <Link
                        to="/ai-powered-system-intelligence-predictive-analytics-center"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setMoreMenuOpen(false)}
                      >
                        <Icon name="Brain" className="w-4 h-4" />
                        AI System Intelligence
                      </Link>
                      <Link
                        to="/critical-infrastructure-monitoring-alert-command-center"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setMoreMenuOpen(false)}
                      >
                        <Icon name="Shield" className="w-4 h-4" />
                        Infrastructure Command Center
                      </Link>
                      <Link
                        to="/authentication-recovery-system-repair-center"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setMoreMenuOpen(false)}
                      >
                        <Icon name="Tool" className="w-4 h-4" />
                        Auth Recovery Center
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <LanguageDropdown />
              <RealtimeNotificationPanel onAlertClick={handleAlertClick} />
              
              {/* User Profile Menu */}
              {user ? (
                <div className="relative hidden lg:block">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-all"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-400 to-blue-400 flex items-center justify-center text-white font-semibold text-sm">
                      {user?.email?.charAt(0)?.toUpperCase()}
                    </div>
                    <Icon name={profileMenuOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
                  </button>

                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-50">
                      <div className="p-4 border-b border-gray-200 dark:border-slate-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.email}</p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/profile"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700 transition-all"
                        >
                          <Icon name="User" size={16} />
                          <span>Profile Settings</span>
                        </Link>
                        <button
                          onClick={async () => {
                            await signOut();
                            setProfileMenuOpen(false);
                            navigate('/login');
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-all"
                        >
                          <Icon name="LogOut" size={16} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-all"
                >
                  <Icon name="LogIn" size={16} />
                  <span>Sign In</span>
                </Link>
              )}
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden flex items-center justify-center h-10 w-10 rounded-lg text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800 transition-all"
              >
                <Icon name={mobileMenuOpen ? "X" : "Menu"} size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="container mx-auto px-4 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {[...primaryNavItems, ...secondaryNavItems]?.map((item) => {
                const isActive = location?.pathname === item?.path;
                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all
                      ${isActive 
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-800'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                  </Link>
                );
              })}
              
              {/* Mobile User Menu */}
              {user ? (
                <>
                  <div className="border-t border-gray-200 dark:border-slate-700 my-2 pt-2">
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-800 transition-all"
                    >
                      <Icon name="User" size={18} />
                      <span>Profile Settings</span>
                    </Link>
                    <button
                      onClick={async () => {
                        await signOut();
                        setMobileMenuOpen(false);
                        navigate('/login');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-all"
                    >
                      <Icon name="LogOut" size={18} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="border-t border-gray-200 dark:border-slate-700 my-2 pt-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-all"
                  >
                    <Icon name="LogIn" size={18} />
                    <span>Sign In</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;